import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dataHealthApi } from '../../services/api';
import { PageHeader, StatCard, Spinner, EmptyState } from '../../components/shared/index';
import clsx from 'clsx';
import {
  ClipboardDocumentCheckIcon,
  FlagIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';

const FRESHNESS = {
  fresh:    { label: 'Fresh',    color: 'bg-green-100 text-green-700 border-green-200' },
  stale:    { label: 'Stale',    color: 'bg-amber-100 text-amber-700 border-amber-200' },
  critical: { label: 'Critical', color: 'bg-red-100   text-red-700   border-red-200'   },
  unknown:  { label: 'Unknown',  color: 'bg-gray-100  text-gray-600  border-gray-200'  },
};

function ReliabilityBar({ value }) {
  const pct = Math.max(0, Math.min(1, Number(value) || 0)) * 100;
  const color = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={clsx('h-full transition-all', color)} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 w-10 text-right">{Math.round(pct)}%</span>
    </div>
  );
}

function Gauge({ score }) {
  const pct = Math.max(0, Math.min(100, score));
  const color = pct >= 80 ? 'text-green-600' : pct >= 50 ? 'text-amber-600' : 'text-red-600';
  const ring  = pct >= 80 ? 'stroke-green-500' : pct >= 50 ? 'stroke-amber-500' : 'stroke-red-500';
  const circumference = 2 * Math.PI * 52;
  const offset = circumference - (pct / 100) * circumference;
  return (
    <div className="relative w-36 h-36">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="52" strokeWidth="10" fill="none" className="stroke-gray-100" />
        <circle cx="60" cy="60" r="52" strokeWidth="10" fill="none" strokeLinecap="round"
          className={clsx('transition-all', ring)}
          strokeDasharray={circumference} strokeDashoffset={offset} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={clsx('text-4xl font-bold', color)}>{Math.round(pct)}</span>
        <span className="text-xs text-gray-500 uppercase tracking-wider">/ 100</span>
      </div>
    </div>
  );
}

export default function DataHealth() {
  const { data, isLoading } = useQuery({
    queryKey: ['data-health'],
    queryFn:  () => dataHealthApi.overview().then(r => r.data),
    refetchInterval: 60000,
  });

  const { integrations = [], cycle_health = null } = data || {};

  const overallScore = useMemo(() => {
    const relevant = integrations.filter(i => typeof i.reliability_score === 'number');
    if (relevant.length === 0) return 0;
    const avg = relevant.reduce((s, i) => s + (i.reliability_score || 0), 0) / relevant.length;
    return Math.max(0, Math.min(100, avg * 100));
  }, [integrations]);

  const recommendations = useMemo(() => {
    const out = [];
    integrations.forEach(i => {
      if (i.is_active && (i.freshness === 'stale' || i.freshness === 'critical')) {
        out.push(`Integration "${i.type}" has not synced in ${Math.round(i.hours_since_sync || 0)} hours — trigger a manual sync or investigate auth.`);
      }
      if (i.is_active && typeof i.reliability_score === 'number' && i.reliability_score < 0.5) {
        out.push(`"${i.type}" reliability is below 50% — verify credentials and API quota.`);
      }
      if (!i.is_active) {
        out.push(`"${i.type}" is disconnected — reconnect to feed signals into the scoring engine.`);
      }
    });
    if (cycle_health) {
      if ((cycle_health.review_completion_pct ?? 0) < 80 && cycle_health.status !== 'closed') {
        out.push(`Cycle "${cycle_health.cycle_name}" review completion is ${Math.round(cycle_health.review_completion_pct || 0)}% — nudge managers to close outstanding reviews.`);
      }
      if ((cycle_health.flag_rate_pct ?? 0) >= 15) {
        out.push(`Flag rate for cycle "${cycle_health.cycle_name}" is unusually high at ${Math.round(cycle_health.flag_rate_pct)}% — investigate calibration inputs.`);
      }
    }
    if (out.length === 0) out.push('All signals look healthy. Keep integrations authenticated and cycle cadence on schedule.');
    return out;
  }, [integrations, cycle_health]);

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader title="Data health" subtitle="Integration freshness, cycle telemetry, and actionable recommendations" />

      <div className="card p-6 mb-6 flex items-center gap-6 flex-wrap">
        <Gauge score={overallScore} />
        <div className="flex-1 min-w-[240px]">
          <h2 className="text-xl font-bold text-gray-900">Overall data health</h2>
          <p className="text-sm text-gray-500 mt-1">
            Averaged from {integrations.length} integration{integrations.length === 1 ? '' : 's'}. Higher scores mean signals flowing into the scoring engine are fresh and reliable.
          </p>
          <div className="mt-3 flex gap-3 flex-wrap">
            <span className="text-xs text-gray-600">Fresh: <span className="font-semibold text-green-600">{integrations.filter(i => i.freshness === 'fresh').length}</span></span>
            <span className="text-xs text-gray-600">Stale: <span className="font-semibold text-amber-600">{integrations.filter(i => i.freshness === 'stale').length}</span></span>
            <span className="text-xs text-gray-600">Critical: <span className="font-semibold text-red-600">{integrations.filter(i => i.freshness === 'critical').length}</span></span>
          </div>
        </div>
      </div>

      <section className="mb-6">
        <h2 className="section-title mb-3">Integrations</h2>
        {integrations.length === 0 ? (
          <div className="card"><EmptyState title="No integrations connected" description="Connect at least one tool to start feeding performance signals into the scoring engine." /></div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {['Integration','Status','Reliability','Freshness','Hours since sync'].map(h => <th key={h} className="th">{h}</th>)}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {integrations.map(i => {
                  const fresh = FRESHNESS[i.freshness] || FRESHNESS.unknown;
                  return (
                    <tr key={i.type} className="tr-hover">
                      <td className="td text-sm font-semibold text-gray-900 capitalize">{i.type.replace(/_/g, ' ')}</td>
                      <td className="td">
                        <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                          i.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600')}>
                          {i.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="td"><ReliabilityBar value={i.reliability_score} /></td>
                      <td className="td">
                        <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border', fresh.color)}>{fresh.label}</span>
                      </td>
                      <td className="td text-xs text-gray-500">{i.hours_since_sync != null ? `${Math.round(i.hours_since_sync)}h` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {cycle_health && (
        <section className="mb-6">
          <h2 className="section-title mb-3">Cycle health — {cycle_health.cycle_name}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard title="Review completion" value={`${Math.round(cycle_health.review_completion_pct || 0)}%`} icon={ClipboardDocumentCheckIcon} color="blue" />
            <StatCard title="Scores computed"   value={cycle_health.scores_computed ?? 0}                         icon={CheckBadgeIcon}              color="green" />
            <StatCard title="Scores with flags" value={cycle_health.scores_with_flags ?? 0}                       icon={FlagIcon}                    color="amber" />
            <StatCard title="Flag rate"         value={`${Math.round(cycle_health.flag_rate_pct || 0)}%`}         icon={ExclamationTriangleIcon}     color={(cycle_health.flag_rate_pct ?? 0) >= 15 ? 'red' : 'blue'} />
          </div>
          <p className="text-xs text-gray-500 mt-2 capitalize">Status: {cycle_health.status}</p>
        </section>
      )}

      <section>
        <h2 className="section-title mb-3 inline-flex items-center gap-2">
          <LightBulbIcon className="w-5 h-5 text-amber-500" />
          Recommendations
        </h2>
        <div className="card p-5">
          <ul className="space-y-2">
            {recommendations.map((r, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
